import { type FormEvent, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router';
import { Alert, Button, Field } from '../components/ui';
import { t } from '../i18n';
import { useAuth } from '../lib/auth';
import { errorMessage } from '../lib/errors';

/** Pantallas de acceso: la marca a la izquierda (arriba en pantallas chicas) y el formulario a la derecha. */
export function AuthShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="grid min-h-full lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
      <header className="flex flex-col justify-between gap-8 bg-tinta px-6 py-8 text-white sm:px-10 lg:px-14 lg:py-14">
        <div>
          <p className="text-5xl font-semibold leading-none tracking-tight sm:text-6xl lg:text-7xl">{t('app.nombre')}</p>
          <span className="mt-5 block h-1 w-12 rounded-full bg-senal" aria-hidden />
        </div>
        <div className="max-w-sm space-y-2">
          <p className="text-base text-white/85">{t('login.descripcion')}</p>
          <p className="text-sm text-white/55">{t('app.lema')}</p>
        </div>
      </header>
      <main className="flex items-center justify-center bg-bruma px-4 py-10 sm:px-8">
        <div className="w-full max-w-sm">
          <h1 className="mb-6 text-2xl font-semibold text-tinta">{title}</h1>
          {children}
        </div>
      </main>
    </div>
  );
}

export function LoginPage() {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (state.status === 'autenticado') return <Navigate to={from} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { mfaToken } = await login(email, password);
      if (mfaToken) navigate('/login/verificacion', { state: { mfaToken, from } });
      else navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setPassword('');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell title={t('login.titulo')}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {state.status === 'anonimo' && state.expired && !error && <Alert tone="info">{t('login.sesion_vencida')}</Alert>}
        {error && <Alert>{error}</Alert>}
        <Field id="correo" label={t('login.correo')} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        <Field
          id="clave"
          label={t('login.clave')}
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <Button type="submit" className="w-full" disabled={busy || !email || !password}>
          {busy ? t('comun.cargando') : t('login.entrar')}
        </Button>
        <Link to="/recuperar-clave" className="block text-center text-sm text-marca hover:underline">
          {t('login.olvido')}
        </Link>
      </form>
    </AuthShell>
  );
}
